/**
 * Hook pour la gestion des retenues de garantie
 */

import { useQuery } from '@tanstack/react-query';
import {
    retentionApi,
    type ConfirmRetentionInput,
    type CreateStandaloneRetentionInput,
    type PendingRetention,
    type ReceiveRetentionInput,
    type RetentionRef,
    type RetentionLetterInput,
} from '@/services/api';
import { useMutationWithToast } from './useMutationWithToast';
import { invoiceKeys } from './useInvoices';
import { forecastKeys } from './useForecast';
import { dashboardActionsKey } from './useDashboardActions';
import { movementKeys } from './useMovements';

// ========================================
// Query Keys
// ========================================

export const retentionKeys = {
    all: ['retention'] as const,
    pending: () => [...retentionKeys.all, 'pending'] as const,
    released: () => [...retentionKeys.all, 'released'] as const,
    aConfirmer: () => [...retentionKeys.all, 'a-confirmer'] as const,
    letterPrefill: (id: string) => [...retentionKeys.all, 'letter-prefill', id] as const,
};

// ========================================
// Hooks
// ========================================

/**
 * Liste des retenues de garantie en attente
 */
export function usePendingRetentions() {
    return useQuery({
        queryKey: retentionKeys.pending(),
        queryFn: () => retentionApi.getPending(),
        staleTime: 30_000,
    });
}

/**
 * Historique des retenues de garantie reçues
 */
export function useReleasedRetentions() {
    return useQuery({
        queryKey: retentionKeys.released(),
        queryFn: () => retentionApi.getReleased(),
        staleTime: 60_000,
    });
}

/**
 * Retenues que l'ancienne tâche de nuit a comptées comme reçues, en attente de la
 * réponse de l'artisan
 */
export function useRetentionsAConfirmer() {
    return useQuery({
        queryKey: retentionKeys.aConfirmer(),
        queryFn: () => retentionApi.getAConfirmer(),
        staleTime: 30_000,
    });
}

/**
 * Ce qu'un changement d'état d'une retenue (reçue, annulée, confirmée) touche :
 * les listes de retenues, le prévisionnel (solde de trésorerie compris), les
 * mouvements du jour, les factures et le tableau de bord.
 */
const CLES_APRES_RECEPTION = [retentionKeys.all, invoiceKeys.lists(), forecastKeys.all, movementKeys.all, dashboardActionsKey] as const;

/**
 * Saisir ou supprimer une retenue reprise change la liste, les comptes « à réclamer »
 * et « à venir » du tableau de bord et la courbe de trésorerie.
 */
const CLES_APRES_SAISIE_REPRISE = [retentionKeys.pending(), forecastKeys.all, dashboardActionsKey] as const;

/**
 * « Reçue » : la retenue est déclarée reçue à une date, sur une facture ou une retenue reprise
 */
export function useReceiveRetention() {
    return useMutationWithToast({
        mutationFn: (input: ReceiveRetentionInput) => retentionApi.markReceived(input),
        operationName: 'Retenue reçue',
        successMessage: 'Retenue enregistrée comme reçue',
        invalidateKeys: CLES_APRES_RECEPTION,
    });
}

/**
 * « Annuler la réception » : la retenue redevient à réclamer
 */
export function useCancelRetentionReceipt() {
    return useMutationWithToast({
        mutationFn: (ref: RetentionRef) => retentionApi.cancelReceipt(ref),
        operationName: 'Annulation de la réception',
        successMessage: 'Réception annulée, la retenue est de nouveau à réclamer',
        invalidateKeys: CLES_APRES_RECEPTION,
    });
}

/**
 * Réponse « Oui, reçue » ou « Non, pas reçue » du bloc de confirmation
 */
export function useConfirmRetention() {
    return useMutationWithToast({
        mutationFn: (input: ConfirmRetentionInput) => retentionApi.confirm(input),
        operationName: 'Confirmation de la retenue',
        successMessage: 'Réponse enregistrée',
        invalidateKeys: CLES_APRES_RECEPTION,
    });
}

/**
 * Saisir une retenue antérieure (standalone)
 */
export function useCreateStandaloneRetention() {
    return useMutationWithToast({
        mutationFn: (input: CreateStandaloneRetentionInput) => retentionApi.createStandalone(input),
        operationName: 'Saisie retenue antérieure',
        successMessage: 'Retenue antérieure enregistrée',
        invalidateKeys: CLES_APRES_SAISIE_REPRISE,
    });
}

/**
 * Payload pré-rempli de la lettre de réclamation pour la retenue sélectionnée
 */
export function useRetentionLetterPrefill(retention: PendingRetention | null) {
    return useQuery({
        queryKey: retentionKeys.letterPrefill(retention?.id ?? ''),
        queryFn: () => retentionApi.getLetterPrefill(retention!.id, retention!.isStandalone),
        enabled: !!retention,
    });
}

/**
 * Générer et télécharger la lettre de réclamation (stateless : aucune invalidation)
 */
export function useDownloadRetentionLetter() {
    return useMutationWithToast({
        mutationFn: (input: RetentionLetterInput) => retentionApi.downloadLetterPdf(input),
        operationName: 'Génération lettre de réclamation',
        successMessage: 'Lettre téléchargée',
        invalidateKeys: [],
    });
}

/**
 * Supprimer une retenue antérieure non reçue (D4)
 */
export function useDeleteStandaloneRetention() {
    return useMutationWithToast({
        mutationFn: (id: string) => retentionApi.deleteStandalone(id),
        operationName: 'Suppression retenue',
        successMessage: 'Retenue antérieure supprimée',
        invalidateKeys: CLES_APRES_SAISIE_REPRISE,
    });
}
